import {PROGRESS_RESULT_VALUES} from '../../../core/entities/entryManagements/constants';
import type {GetEntryManagementResponse} from '../../../core/repositories/mypageEntryManagementsRepository';

/**
 * 指定されたステップが最終ステップかどうかを判定する
 *
 * @param steps - ステップの配列
 * @param targetStepOrder - 判定対象のステップのorder
 * @returns 最終ステップの場合true
 */
export const isLastStep = (
  steps: NonNullable<GetEntryManagementResponse['steps']>,
  targetStepOrder: number,
): boolean => {
  if (!steps || steps.length === 0) {
    return false;
  }

  const maxStepOrder = Math.max(...steps.map((step) => step.stepOrder));
  return targetStepOrder === maxStepOrder;
};

/**
 * モーダルを表示すべき結果かを判定する
 * 空または「結果待ち」の場合はモーダルを表示しない
 *
 * @param result - 判定対象の結果
 * @returns モーダルを表示すべき場合true
 */
export const shouldShowModal = (result: string | null): boolean => {
  return (
    result !== '' &&
    result !== null &&
    result !== PROGRESS_RESULT_VALUES.pending
  );
};

/**
 * 結果が落選または辞退かどうかを判定する
 *
 * @param result - 判定対象の結果
 * @returns 落選または辞退の場合true
 */
export const isRejectedOrDeclined = (result: string | null): boolean => {
  return (
    result === PROGRESS_RESULT_VALUES.rejected ||
    result === PROGRESS_RESULT_VALUES.declined
  );
};

/**
 * 作成から1分以内のステップかどうかを判定
 *
 * @param createdAt - ステップの作成日時（ISO 8601形式）
 * @returns 1分以内に作成されたステップの場合true、無効な日付の場合false
 */
export const isNewlyCreatedStep = (createdAt: string): boolean => {
  const created = new Date(createdAt);
  const now = new Date();

  // 無効な日付をチェック
  if (Number.isNaN(created.getTime()) || Number.isNaN(now.getTime())) {
    return false;
  }

  // 未来の日付をチェック
  if (created.getTime() > now.getTime()) {
    return false;
  }

  const diffMinutes = (now.getTime() - created.getTime()) / 1000 / 60;
  return diffMinutes < 1;
};

/**
 * ステップ配列から最新の作成ステップIDを取得
 * 同じタイムスタンプの場合はIDが大きい方（より最近作成された方）を返す
 *
 * @param steps - ステップの配列
 * @returns 最新の新規作成ステップID、存在しない場合null
 */
export const getLatestCreatedStepId = (
  steps: NonNullable<GetEntryManagementResponse['steps']>,
): number | null => {
  const newlyCreatedSteps = steps.filter((step) =>
    isNewlyCreatedStep(step.created),
  );

  if (newlyCreatedSteps.length === 0) {
    return null;
  }

  return newlyCreatedSteps.reduce((latest, current) => {
    const latestTime = new Date(latest.created).getTime();
    const currentTime = new Date(current.created).getTime();

    // 同じタイムスタンプの場合はIDで比較
    if (latestTime === currentTime) {
      return current.id > latest.id ? current : latest;
    }

    return currentTime > latestTime ? current : latest;
  }).id;
};
